import { useRef, useState, useEffect, RefObject, MutableRefObject } from 'react';
import * as THREE from 'three';
import { useAppStore, interpolateNum } from '../store/useAppStore';
import { viewportActions } from '../viewportActions';
import { HERO_POSITIONS, HERO_SNAP_RAD, computeHeroFade, HeroPosition } from '../heroConstants';

interface CameraControlInput {
  cameraRef: RefObject<THREE.Camera | null>;
  controlsRef: RefObject<any>;
  invalidateRef?: MutableRefObject<() => void>;
}

interface CameraControlOutput {
  camPos: { x: number; y: number; z: number };
  selectedHeroId: string | null;
  heroBgColor: string;
  heroBgAlpha: number;
  applyCamPos: (axis: 'x' | 'y' | 'z', val: number) => void;
  animateCameraTo: (target: THREE.Vector3, duration?: number) => void;
  syncCamRef: MutableRefObject<() => void>;
  resetHeroState: () => void;
  cameraTrackEnabled: boolean;
  setCameraTrackEnabled: (v: boolean) => void;
}

export function useCameraControl({ cameraRef, controlsRef, invalidateRef }: CameraControlInput): CameraControlOutput {
  const [camPos, setCamPos] = useState({
    x: HERO_POSITIONS[0].position.x,
    y: HERO_POSITIONS[0].position.y,
    z: HERO_POSITIONS[0].position.z,
  });
  const [, setCamRot] = useState({ x: 0, y: 0, z: 0 });
  const [selectedHeroId, setSelectedHeroId] = useState<string | null>(HERO_POSITIONS[0].id);
  const [heroBgColor, setHeroBgColor] = useState<string>('#000000');
  const [heroBgAlpha, setHeroBgAlpha] = useState<number>(0);

  const isAnimatingCameraRef = useRef(false);
  const snapCheckTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const syncCamRef = useRef<() => void>(() => {});

  const heroFeaturesEnabled = useAppStore((s) => s.heroFeaturesEnabled);
  const cameraTrackEnabled = useAppStore((s) => s.cameraTrackEnabled);
  const setCameraTrackEnabled = useAppStore((s) => s.setCameraTrackEnabled);
  const cameraKeyframes = useAppStore((s) => s.cameraKeyframes);
  const currentFrame = useAppStore((s) => s.currentFrame);

  // When hero features are disabled, clear bg fade and deselect hero.
  useEffect(() => {
    if (!heroFeaturesEnabled) {
      setHeroBgAlpha(0);
      setSelectedHeroId(null);
    }
  }, [heroFeaturesEnabled]);

  /** Smoothly animate the viewport camera to `target` in ~400 ms. */
  const animateCameraTo = (target: THREE.Vector3, duration = 400) => {
    const cam = cameraRef.current;
    if (!cam) return;
    isAnimatingCameraRef.current = true;
    const start = cam.position.clone();
    const t0 = performance.now();
    const tick = () => {
      const t = Math.min((performance.now() - t0) / duration, 1);
      const ease = 1 - Math.pow(1 - t, 3); // ease-out cubic
      cam.position.lerpVectors(start, target, ease);
      cam.lookAt(0, 0, 0);
      controlsRef.current?.update();
      const p2 = cam.position; const r2 = cam.rotation as THREE.Euler;
      setCamPos({ x: +p2.x.toFixed(3), y: +p2.y.toFixed(3), z: +p2.z.toFixed(3) });
      setCamRot({
        x: +(THREE.MathUtils.radToDeg(r2.x)).toFixed(1),
        y: +(THREE.MathUtils.radToDeg(r2.y)).toFixed(1),
        z: +(THREE.MathUtils.radToDeg(r2.z)).toFixed(1),
      });
      // Update hero background fade during animation.
      if (heroFeaturesEnabled) {
        const { hero: fadeHero, alpha: fadeAlpha } = computeHeroFade(cam.position);
        if (fadeHero) { setHeroBgColor(fadeHero.color); setHeroBgAlpha(fadeAlpha); }
        else { setHeroBgAlpha(0); }
      }
      // Invalidate so R3F redraws each animation frame in demand mode.
      invalidateRef?.current();
      if (t < 1) { requestAnimationFrame(tick); }
      else { isAnimatingCameraRef.current = false; }
    };
    requestAnimationFrame(tick);
  };

  const syncCameraState = () => {
    const cam = cameraRef.current;
    if (!cam) return;
    const p = cam.position;
    const r = cam.rotation;
    const newPos = { x: +p.x.toFixed(3), y: +p.y.toFixed(3), z: +p.z.toFixed(3) };
    setCamPos(newPos);
    useAppStore.getState().setCurrentCamPos(newPos);
    setCamRot({
      x: +(THREE.MathUtils.radToDeg(r.x)).toFixed(1),
      y: +(THREE.MathUtils.radToDeg(r.y)).toFixed(1),
      z: +(THREE.MathUtils.radToDeg(r.z)).toFixed(1),
    });
    // Hero background: fade in continuously as camera approaches a hero view.
    if (heroFeaturesEnabled) {
      const { hero: fadeHero, alpha: fadeAlpha } = computeHeroFade(cam.position);
      if (fadeHero) { setHeroBgColor(fadeHero.color); setHeroBgAlpha(fadeAlpha); }
      else { setHeroBgAlpha(0); }
    } else {
      setHeroBgAlpha(0);
    }
    // Clear hero label immediately if camera is no longer within snap range.
    if (!isAnimatingCameraRef.current) {
      const camNormImmediate = cam.position.clone().normalize();
      const stillNearHero = heroFeaturesEnabled && HERO_POSITIONS.some((h) => {
        const hDir = new THREE.Vector3(h.position.x, h.position.y, h.position.z).normalize();
        return Math.acos(Math.max(-1, Math.min(1, camNormImmediate.dot(hDir)))) < HERO_SNAP_RAD;
      });
      if (!stillNearHero) setSelectedHeroId(null);
    }
    // Soft-snap: 150 ms after orbit settles, animate camera toward the nearest
    // hero position that is within 5 degrees of the current camera direction.
    if (heroFeaturesEnabled && !isAnimatingCameraRef.current) {
      if (snapCheckTimeoutRef.current) clearTimeout(snapCheckTimeoutRef.current);
      snapCheckTimeoutRef.current = setTimeout(() => {
        const camera = cameraRef.current;
        if (!camera) return;
        const heroes = HERO_POSITIONS;
        if (!heroes.length) return;
        const camNorm = camera.position.clone().normalize();
        let best: HeroPosition | null = null;
        let bestAngle = Infinity;
        for (const h of heroes) {
          const hDir = new THREE.Vector3(h.position.x, h.position.y, h.position.z).normalize();
          const angle = Math.acos(Math.max(-1, Math.min(1, camNorm.dot(hDir))));
          if (angle < HERO_SNAP_RAD && angle < bestAngle) { bestAngle = angle; best = h; }
        }
        if (best) {
          animateCameraTo(new THREE.Vector3(best.position.x, best.position.y, best.position.z));
          setSelectedHeroId(best.id);
        }
      }, 150);
    }
  };
  // Keep syncCamRef current every render so OrbitControls / arrow-key handlers
  // always call the latest closure without needing to be recreated.
  syncCamRef.current = syncCameraState;

  const applyCamPos = (axis: 'x' | 'y' | 'z', val: number) => {
    const cam = cameraRef.current;
    if (!cam) return;
    cam.position[axis] = val;
    cam.lookAt(0, 0, 0);
    controlsRef.current?.update();
    syncCameraState();
    invalidateRef?.current();
  };

  // Apply camera keyframe animation during scrub / playback.
  useEffect(() => {
    if (!cameraTrackEnabled) return;
    const frames = Object.keys(cameraKeyframes).map(Number);
    if (!frames.length) return;
    const xMap: Record<number, number> = {};
    const yMap: Record<number, number> = {};
    const zMap: Record<number, number> = {};
    const fovMap: Record<number, number> = {};
    for (const [f, v] of Object.entries(cameraKeyframes)) {
      const fn = Number(f);
      xMap[fn] = v.x; yMap[fn] = v.y; zMap[fn] = v.z; fovMap[fn] = v.fov;
    }
    const xv = interpolateNum(xMap, currentFrame);
    const yv = interpolateNum(yMap, currentFrame);
    const zv = interpolateNum(zMap, currentFrame);
    const fovV = interpolateNum(fovMap, currentFrame);
    if (xv !== null) applyCamPos('x', xv);
    if (yv !== null) applyCamPos('y', yv);
    if (zv !== null) applyCamPos('z', zv);
    if (fovV !== null) useAppStore.getState().setCameraFov(Math.round(fovV));
  // applyCamPos is a plain fn reading only refs and setState; stable exclusion.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFrame, cameraKeyframes, cameraTrackEnabled]);

  // Register the snap-to-hero viewport action.
  viewportActions.snapToHero.current = (id: string) => {
    const h = HERO_POSITIONS.find((hp) => hp.id === id);
    if (h) {
      setSelectedHeroId(id);
      if (controlsRef.current) controlsRef.current.target.set(0, 0, 0);
      animateCameraTo(new THREE.Vector3(h.position.x, h.position.y, h.position.z));
      useAppStore.getState().setCameraFov(h.fov);
    }
  };

  const resetHeroState = () => {
    setSelectedHeroId(null);
    setHeroBgAlpha(0);
  };

  return {
    camPos,
    selectedHeroId,
    heroBgColor,
    heroBgAlpha,
    applyCamPos,
    animateCameraTo,
    syncCamRef,
    resetHeroState,
    cameraTrackEnabled,
    setCameraTrackEnabled,
  };
}
